import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {NewProjectContext} from './utils/NewProjectContext';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Close from '@mui/icons-material/Close';
import ProjectSelectionStep from './steps/ProjectSelectionStep';
import ProjectQuestionsStep from './steps/ProjectQuestionsStep';
import AnonProjectLogin from './steps/AnonProjectLogin';
import NewProjectChoice from './components/NewProjectChoice';
import AlertDialog from '../../common/dialogs/alertDialog';
import {useSetRecoilState} from 'recoil';
import {reloadRfpsState} from '../../recoil/reloadRfps/reloadRfpsAtom';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {API_URL} from '../../constants';
import {loadingState} from '../../recoil/bidmiiCustomLoader/bidmiiCustomLoaderAtom';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import {makeStyles} from '@mui/styles';
import useCelebrationLottie from '../../hooks/useCelebrationLottie';
import useSnackbar from '../../hooks/useSnackbar';

const Transition = React.forwardRef(function Transition(props, ref) {
  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children?: ReactNode; direction: "left"; r... Remove this comment to see the full error message
  return <Slide direction="left" ref={ref} {...props} />;
});

const useStyles = (homeDepot: boolean) =>
  makeStyles(() => ({
    container: {
      '& *': {
        fontFamily: homeDepot
          ? 'Helvetica Neue, Helvetica !important'
          : undefined,
      },
    },
  }));

interface IProps {
  iframe?: boolean;
}

const ProjectCreationWizard = (props: IProps) => {
  const history = useHistory();
  const setReloadRfps = useSetRecoilState(reloadRfpsState);
  const {openSnackbar} = useSnackbar();
  const setLoading = useSetRecoilState(loadingState);

  const {project, setProject, newProjectStep, setNewProjectStep, homeDepot} =
    useContext(NewProjectContext);
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [newProjectChoiceOpen, setNewProjectChoiceOpen] = useState(false);
  const [saveDraftOpen, setSaveDraftOpen] = useState(false);

  const [celebrationAnimation, startCelebration] = useCelebrationLottie({});
  const classes = useStyles(homeDepot)();

  const handleClose = () => {
    setNewProjectStep(0);
    history.goBack();
  };

  const handleClickCloseButton = () => {
    if (project.id) {
      setSaveDraftOpen(true);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    const localRfp =
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
      localStorage.getItem('rfp') && JSON.parse(localStorage.getItem('rfp'));
    if (localRfp?.type === 'BIDMII_DIRECT') {
      setProject({...localRfp});
      setNewProjectStep(1);
      return;
    }
    if (
      !homeDepot &&
      localRfp &&
      moment(localRfp.createdDate).isAfter(moment().subtract(2, 'days'))
    ) {
      setNewProjectChoiceOpen(true);
    } else {
      startProjectFromScratch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startProjectFromScratch = (title?: string) => {
    setNewProjectStep(1);
    setProject({title: title || null});
    localStorage.removeItem('rfp');
    localStorage.removeItem('prePackagedProject');
    setNewProjectChoiceOpen(false);
  };

  const params = new URLSearchParams(window.location.search);
  const title = params.has('title') ? params.get('title') : null;

  useEffect(() => {
    if (title) {
      startProjectFromScratch(title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  const discardProjectDraft = () => {
    setLoading(true);
    securedFetchRequest(`${API_URL}/rfps/${project.uid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({enabled: false}),
    })
      .then(() => {
        setSaveDraftOpen(false);
        setLoading(false);
        localStorage.removeItem('rfp');
        localStorage.removeItem('prePackagedProject');
        setReloadRfps(true);
        openSnackbar('This draft has been discarded.');
        handleClose();
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'NewProjectWizard.tsx discardProjectDraft()');
          Sentry.captureException(err);
        });
      });
  };

  const chooseProjectTemplate = (template: $TSFixMe) => {
    setTemplateKey(template);
    setNewProjectStep(1);
  };

  useEffect(() => {
    if (newProjectStep === 3) {
      startCelebration();
    }
  }, [newProjectStep, startCelebration]);

  const getProjectStep = () => {
    switch (newProjectStep) {
      case 0:
        return (
          <ProjectSelectionStep
            handleClose={handleClose}
            chooseProjectTemplate={chooseProjectTemplate}
          />
        );
      case 1:
        return (
          <ProjectQuestionsStep
            templateKey={templateKey}
            skipTitle={title != null}
          />
        );
      case 2:
        return (
          <AnonProjectLogin handleClose={handleClose} iframe={props.iframe} />
        );
      // HOME DEPOT TEMP POST-SUBMIT PAGE
      case 3:
        return (
          <div
            style={{
              backgroundColor: '#f96302',
              width: '100vw',
              height: '100vh',
              margin: '0 -16px',
            }}>
            {celebrationAnimation}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={handleClickCloseButton}
        aria-labelledby="New Project Creation"
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'ForwardRefExoticComponent<RefAttributes<unkn... Remove this comment to see the full error message
        TransitionComponent={Transition}
        PaperProps={{
          style: {overflowX: 'hidden'},
          elevation: props.iframe ? 0 : 3,
        }}
        fullScreen>
        {!props.iframe && newProjectStep < 3 && (
          <IconButton
            onClick={handleClickCloseButton}
            style={{position: 'absolute', right: 12, top: 12}}
            size="large">
            <Close color="primary" />
          </IconButton>
        )}

        <Box px={2} className={classes.container}>
          {getProjectStep()}
        </Box>
      </Dialog>

      <NewProjectChoice
        open={newProjectChoiceOpen}
        setNewProjectChoiceOpen={setNewProjectChoiceOpen}
        startProjectFromScratch={startProjectFromScratch}
        handleClose={handleClose}
        chooseProjectTemplate={chooseProjectTemplate}
        iframe={props.iframe}
      />

      <AlertDialog
        open={saveDraftOpen}
        title="Save this draft?"
        body="Would you like to save this project as a draft?"
        close="DISCARD"
        onClose={discardProjectDraft}
        confirm="SAVE"
        onConfirm={() => {
          handleClose();
          history.push(`/myRfps/${project.uid}`);
          setReloadRfps(true);
        }}
      />
    </>
  );
};

export default ProjectCreationWizard;
